/**
 * Star Component
 *
 * SVG star icon for rating displays.
 * Supports filled, half-filled, and empty states.
 *
 * Feature: 649-critique-feedback-options
 */

import { cn } from '@/lib/utils';

export type StarFill = 'empty' | 'half' | 'full';

export interface StarProps {
  /** Fill state: empty, half, or full */
  fill?: StarFill;
  /** Size in pixels (width and height) */
  size?: number;
  /** Additional CSS classes */
  className?: string;
  /** Color for filled portion (CSS color value) */
  fillColor?: string;
  /** Color for empty portion (CSS color value) */
  emptyColor?: string;
  /** Accessible label */
  'aria-label'?: string;
}

/**
 * Star primitive component for rating displays
 *
 * Uses SVG with gradient for half-fill support.
 * Designed for composition into StarRating component.
 */
export function Star({
  fill = 'empty',
  size = 24,
  className,
  fillColor = 'currentColor',
  emptyColor = 'transparent',
  'aria-label': ariaLabel,
}: StarProps) {
  // Generate unique ID for gradient (needed when multiple stars on page)
  const gradientId = `star-gradient-${Math.random().toString(36).slice(2, 9)}`;

  // Determine fill percentage for gradient
  const fillPercent = fill === 'full' ? 100 : fill === 'half' ? 50 : 0;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('inline-block', className)}
      aria-label={ariaLabel}
      role="img"
    >
      {/* Define gradient for partial fill */}
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset={`${fillPercent}%`} stopColor={fillColor} />
          <stop offset={`${fillPercent}%`} stopColor={emptyColor} />
        </linearGradient>
      </defs>

      {/* Star path - 5-pointed star */}
      <path
        d="M12 2L14.9 8.62L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L9.1 8.62L12 2Z"
        fill={fill === 'empty' ? emptyColor : `url(#${gradientId})`}
        stroke={fillColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

Star.displayName = 'Star';
